import { useState } from 'react';
import { useQuery } from 'react-query';

import { useAuth } from '@src/features/auth/contexts/auth-context';
import { useLocale } from '@src/hooks/use-locale';
import { queryClient } from '@src/query-client';
import { tripService } from '@src/services';
import { Trip } from '@src/types/api/models';

import { TripContext } from './trip.context';
import { TripPollingCondition, TripPollingOptions, TripProviderProps } from './types';

const getKey = (trip: Trip | null, locale: string) => ['trip', trip?.id, locale];
export const TripProvider = ({ value, children }: TripProviderProps) => {
  const { trip } = value;
  const { isReady } = useAuth();
  const locale = useLocale();

  const queryKey = getKey(trip, locale);
  const queryFn = () => tripService.getTripById(trip?.id as string, locale);
  const queryOptions = {
    initialData: () => trip,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!trip,
  };
  const tripQuery = useQuery(queryKey, queryFn, queryOptions);

  /**
   * Trips can be polled until a certain condition is met.
   * The condition should return false if no further polling is needed or a timeout in ms to poll again.
   * The Provider returns a function pollTrip(condition) that returns a Promise and resolves when the condition is met.
   */
  const [tripPollingCondition, setTripPollingCondition] = useState<TripPollingCondition | undefined>(undefined);
  const [pollingResolver, setPollingResolver] = useState<(trip: Trip) => void>();
  useQuery([...queryKey, 'polling'], queryFn, {
    ...queryOptions,
    enabled: queryOptions.enabled && !!tripPollingCondition,
    refetchInterval: tripPollingCondition,
    refetchIntervalInBackground: true,
    onSuccess: (data) => {
      if (data && tripPollingCondition?.(data) === false) {
        pollingResolver?.(data);
        queryClient.setQueryData(queryKey, data);
        setTripPollingCondition(undefined);
      }
    },
  });

  const linkedTripsQuery = useQuery(
    ['linkedTrips', trip?.id],
    () => {
      return tripService.getLinkedTrips(trip?.id as string, locale);
    },

    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: !!trip && isReady,
    },
  );

  if (!tripQuery.data) {
    return <TripContext.Provider value={null}>{children}</TripContext.Provider>;
  }

  return (
    <TripContext.Provider
      value={{
        trip: tripQuery.data,
        linkedTrips: linkedTripsQuery?.data,
        isLoading: linkedTripsQuery.isLoading || linkedTripsQuery.isFetching,
        updateTrip: async () => {
          void queryClient.invalidateQueries(queryKey);
          await tripQuery.refetch();

          setTimeout(() => queryClient.invalidateQueries(['linkedTrips', tripQuery.data?.id]), 5000);
        },
        pollTrip: async (until: TripPollingCondition, options?: TripPollingOptions): Promise<Trip> => {
          setTimeout(() => {
            setTripPollingCondition(() => until);
          }, options?.initialDelay ?? 0);

          return new Promise((resolve) => {
            setPollingResolver(() => resolve);
          });
        },
      }}
    >
      {children}
    </TripContext.Provider>
  );
};
